import type { KeyboardEvent, MouseEvent } from "react";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";
import styled, { css, useTheme } from "styled-components/macro";
import {
  PrimaryButtonSmallV2,
  DestructiveButtonSmallV2,
} from "../Buttons/Buttons";
import { Text } from "../Typography/TypographyV2";
import type {
  NumericFilterCriterion,
  NumericFilterOperator,
} from "../../types/types";
import type { ChipType } from "../Chips/Chips";
import { useOnClickOutside } from "../../util/hooks";
import { usePortalPopoverPosition } from "../../util/hooks/usePortalPopoverPosition";
import { usePopoverFocus } from "../../util/hooks/usePopoverFocus";
import { DropDownModal } from "../DropDownModal/DropDownModal";
import { CaretDownIcon } from "../Icons/Icons";
import { DropDownWrapper } from "../FilterBy/filter_by.utils";
import { FilterButton } from "../FilterBy/FilterBy";
import {
  decodeNumericFilterCriterion,
  encodeNumericFilterCriterion,
  isRangeOperator,
  isValidNumericRange,
} from "./filterutils";

/**
 * AP-1016 (Advanced Filters, Part 1) — numeric attribute values accept a
 * decimal point, a leading negative sign, and scientific notation (e/E,
 * optional +/-); no commas. Matches the format the numeric attribute value
 * inputs elsewhere in PIM already rely on, applied here as an explicit guard
 * since this input isn't inside a form.
 *
 * AD-9618 — the inputs are `type="text"` rather than `type="number"`: a
 * native number input reports non-numeric text (e.g. "abc") as `""`, which
 * this component can't tell apart from a blank bound, so the bad value was
 * silently dropped instead of being flagged. Keep the raw text flowing
 * through this regex.
 */
export const NUMERIC_FILTER_VALUE_REGEX = /^-?\d+(\.\d+)?([eE][+-]?\d+)?$/;

// The regex alone admits values like "1e400" that overflow to Infinity.
function isValidNumberString(raw: string): boolean {
  const trimmed = raw.trim();
  return (
    trimmed.length > 0 &&
    NUMERIC_FILTER_VALUE_REGEX.test(trimmed) &&
    Number.isFinite(Number(trimmed))
  );
}

// Confluence's Operators & Match Rules table — labels for all four
// operators; which of them actually show up in a given dropdown is driven
// by the `operators` prop (from the backend's `numeric_filters` response),
// not by this map, which only supplies display text.
const OPERATOR_LABELS: Record<NumericFilterOperator, string> = {
  is: "Is",
  is_exactly: "Is exactly",
  between: "Between",
  overlaps: "Overlaps",
};

const Wrapper = styled.div<{ $inline: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.resolve("spacing_md")};
  padding: ${({ theme, $inline }) =>
    theme.resolve($inline ? "spacing_none" : "spacing_xl")};
`;

// Shared border/background/state styling for the operator trigger and the
// value/min/max inputs. Uses the `color_border_input*` / `color_bg_surface_*`
// tokens, not `color_filterPanel_*` — those are for the outer panel chrome,
// not form controls.
const fieldStateStyles = css`
  border-radius: ${({ theme }) => theme.resolve("radius_xs")};
  border: ${({ theme }) => theme.resolve("border_md")} solid
    ${({ theme }) => theme.resolve("color_border_input")};
  background: ${({ theme }) => theme.resolve("color_bg_surface_standard")};
  font-family: ${({ theme }) => theme.resolve("font_family_primary")};
  font-size: ${({ theme }) => theme.resolve("font_size_md")};
  line-height: ${({ theme }) => theme.resolve("line_height_md")};
  font-weight: ${({ theme }) => theme.resolve("font_weight_regular")};
  color: ${({ theme }) => theme.resolve("color_text_primary")};

  &:hover:not(:disabled) {
    border-color: ${({ theme }) => theme.resolve("color_border_dark")};
  }

  &:focus,
  &:focus-visible {
    outline: none;
    border-color: ${({ theme }) => theme.resolve("color_border_input_active")};
  }

  &:disabled {
    cursor: not-allowed;
    background: ${({ theme }) => theme.resolve("color_bg_surface_disabled")};
    color: ${({ theme }) => theme.resolve("color_text_tertiary")};
  }
`;

// Matches the Q3 Figma file's numeric filter inputs: a single row of
// `[Min] to [Max]` (or a lone value field), placeholder-labeled rather than
// wrapped in a separate text label above each field.
const NumericInput = styled.input<{ $invalid?: boolean }>`
  flex: 1;
  min-width: 0;
  padding: ${({ theme }) => theme.resolve("spacing_xs")}
    ${({ theme }) => theme.resolve("spacing_md")};
  ${fieldStateStyles}
  ${({ $invalid, theme }) =>
    $invalid &&
    css`
      border-color: ${theme.resolve("color_border_input_error")};
    `}

  ::placeholder {
    color: ${({ theme }) => theme.resolve("color_text_placeholder")};
  }
`;

const OperatorFieldWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const OperatorTrigger = styled.button<{ $open?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.resolve("spacing_md")};
  width: 100%;
  padding: ${({ theme }) => theme.resolve("spacing_xs")}
    ${({ theme }) => theme.resolve("spacing_md")};
  text-align: left;
  cursor: pointer;
  ${fieldStateStyles}
  ${({ $open, theme }) =>
    $open &&
    css`
      border-color: ${theme.resolve("color_border_input_active")};
    `}
`;

const OperatorLabel = styled.span`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const OperatorIcon = styled.span`
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
`;

// No portal (unlike DropDown.tsx), so it can hang past the bottom of a
// viewport-fixed NumericFilterButton popover — `$openUp` flips it above the
// operator field when there's no room below (see `operatorMenuRef` effect).
const OperatorMenuWrapper = styled.div<{ $openUp: boolean }>`
  position: absolute;
  left: 0;
  right: 0;
  z-index: 500;
  ${({ $openUp, theme }) =>
    $openUp
      ? css`
          bottom: 100%;
          margin-bottom: ${theme.resolve("spacing_xs")};
        `
      : css`
          top: 100%;
          margin-top: ${theme.resolve("spacing_xs")};
        `}
`;

const ValueRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.resolve("spacing_sm")};
`;

const ActionsRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.resolve("spacing_md")};
  padding-top: ${({ theme }) => theme.resolve("spacing_md")};
`;

// Sized to the numeric filter's own spec (320px) rather than the wider
// generic panel wrapper `FilterBy` uses for its checkbox-style dropdowns.
const NumericFilterPopover = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  margin-top: ${({ theme }) => theme.resolve("spacing_xs")};
  width: ${({ theme }) => theme.resolve("width_xxs")};
  background: ${({ theme }) => theme.resolve("color_bg_surface_standard")};
  border: ${({ theme }) => theme.resolve("border_md")} solid
    ${({ theme }) => theme.resolve("color_border_lighter")};
  border-radius: ${({ theme }) => theme.resolve("radius_xs")};
  box-shadow: 0px 4px 8px 0px rgba(0, 0, 0, 0.12);
  z-index: 500;
  /* Focused programmatically (tabIndex -1) on a pointer open; not a tab stop. */
  &:focus {
    outline: none;
  }
`;

interface INumericFilterInput {
  /** The filter key this input belongs to, used only to keep testids unique. */
  filterType: string;
  /**
   * Which operators to offer, in order — sourced from the backend's
   * `numeric_filters[key].operators` (product/MDM listings) or
   * `NUMERIC_FILTER_OPERATORS` (collection tables, which have no backend
   * equivalent — see NumericFilterButton). Never hardcode this list inline.
   */
  operators: NumericFilterOperator[];
  /** Currently applied criterion, if any — prefills the inputs on mount. */
  initialCriterion: NumericFilterCriterion | null;
  onApply: (criterion: NumericFilterCriterion) => void;
  onClear: () => void;
  /**
   * Renders in-flow (no popover padding, no Apply/Clear buttons) and calls
   * `onApply`/`onClear` on every edit instead — for hosts that batch-apply
   * on their own action, e.g. the Advanced Filters modal's "Search".
   */
  inline?: boolean;
  defaultOperator?: NumericFilterOperator;
  /**
   * Reports whether the fields currently hold a malformed or inverted value.
   * Inline hosts need this to block their own submit, since `onClear` alone
   * can't distinguish "left blank" from "typed something invalid". Called
   * with `false` on unmount.
   */
  onInvalidChange?: (isInvalid: boolean) => void;
}

/**
 * AP-1016 (Advanced Filters) — operator + value-entry UI shared between
 * Part 1 (embedded in the existing filter panels, see FilterGroup.tsx and
 * FilterBy.tsx) and Part 2 (the Advanced Filters modal, once built). Keep
 * the operator/value shape here so both parts compile criteria identically
 * instead of re-deriving the logic.
 *
 * Per the Confluence spec's Operators & Match Rules: `is`/`is_exactly` each
 * take one value; `between`/`overlaps` take Min and/or Max — both filled is
 * a closed range, either one alone is an open-ended "at least"/"at most"
 * query, and both blank isn't submittable. When both are filled, an
 * inverted range (Min > Max) is rejected client-side, since the backend
 * would otherwise silently return zero results rather than erroring.
 */
// Derives the operator/value/min/max field state from an applied criterion —
// shared by the initial `useState` and the resync effect below so both stay
// in lockstep with `decodeNumericFilterCriterion`'s shape.
function deriveFieldsFromCriterion(
  criterion: NumericFilterCriterion | null,
  operators: NumericFilterOperator[],
  defaultOperator?: NumericFilterOperator
): {
  operator: NumericFilterOperator;
  value: string;
  min: string;
  max: string;
} {
  return {
    operator:
      criterion && operators.includes(criterion.operator)
        ? criterion.operator
        : defaultOperator ?? operators[0],
    value:
      criterion &&
      criterion.operator !== "between" &&
      criterion.operator !== "overlaps"
        ? String(criterion.value)
        : "",
    min:
      criterion &&
      (criterion.operator === "between" || criterion.operator === "overlaps") &&
      criterion.min !== undefined
        ? String(criterion.min)
        : "",
    max:
      criterion &&
      (criterion.operator === "between" || criterion.operator === "overlaps") &&
      criterion.max !== undefined
        ? String(criterion.max)
        : "",
  };
}

export const NumericFilterInput = ({
  filterType,
  operators,
  initialCriterion,
  onApply,
  onClear,
  inline = false,
  defaultOperator,
  onInvalidChange,
}: INumericFilterInput) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const [operator, setOperator] = useState<NumericFilterOperator>(
    () =>
      deriveFieldsFromCriterion(initialCriterion, operators, defaultOperator)
        .operator
  );
  const [value, setValue] = useState(
    () =>
      deriveFieldsFromCriterion(initialCriterion, operators, defaultOperator)
        .value
  );
  const [min, setMin] = useState(
    () =>
      deriveFieldsFromCriterion(initialCriterion, operators, defaultOperator)
        .min
  );
  const [max, setMax] = useState(
    () =>
      deriveFieldsFromCriterion(initialCriterion, operators, defaultOperator)
        .max
  );

  const initialCriterionKey = initialCriterion
    ? encodeNumericFilterCriterion(initialCriterion)
    : null;
  // In inline mode `initialCriterion` echoes our own onApply/onClear calls
  // back; resyncing on those would rewrite what the user is typing (e.g.
  // "1e3" → "1000", or a half-typed value wiped to "").
  const selfAppliedKeyRef = useRef<string | null | undefined>(undefined);
  useEffect(() => {
    if (inline && initialCriterionKey === selfAppliedKeyRef.current) return;
    const derived = deriveFieldsFromCriterion(
      initialCriterion,
      operators,
      defaultOperator
    );
    setOperator(derived.operator);
    setValue(derived.value);
    setMin(derived.min);
    setMax(derived.max);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialCriterionKey]);

  const [showOperatorMenu, setShowOperatorMenu] = useState(false);
  const operatorFieldRef = useRef<HTMLDivElement>(null);
  useOnClickOutside(operatorFieldRef, () => setShowOperatorMenu(false));
  const operatorMenuRef = useRef<HTMLDivElement>(null);
  const [operatorMenuOpenUp, setOperatorMenuOpenUp] = useState(false);
  useLayoutEffect(() => {
    const field = operatorFieldRef.current;
    const menu = operatorMenuRef.current;
    if (!showOperatorMenu || !field || !menu) {
      setOperatorMenuOpenUp(false);
      return;
    }
    const fieldRect = field.getBoundingClientRect();
    const menuHeight = menu.offsetHeight;
    setOperatorMenuOpenUp(
      fieldRect.bottom + menuHeight > window.innerHeight &&
        fieldRect.top - menuHeight >= 0
    );
  }, [showOperatorMenu]);

  const isRangeSelected = isRangeOperator(operator);
  const minFilled = isValidNumberString(min);
  const maxFilled = isValidNumberString(max);

  // Inversion only makes sense once both bounds are filled — a single-bound
  // (open-ended) range can't be inverted.
  const isInvertedRange = useMemo(
    () =>
      isRangeSelected &&
      minFilled &&
      maxFilled &&
      !isValidNumericRange(Number(min), Number(max)),
    [isRangeSelected, minFilled, maxFilled, min, max]
  );

  // A range is valid once at least one bound is a well-formed number (per
  // `hasNumericBound`) and, if both are filled, they aren't inverted. An
  // empty bound is fine — that's the open-ended "at least"/"at most" case —
  // but a bound with a malformed, non-empty value is not.
  const isValid = useMemo(() => {
    if (isRangeSelected) {
      const minOk = min.trim().length === 0 || minFilled;
      const maxOk = max.trim().length === 0 || maxFilled;
      return minOk && maxOk && (minFilled || maxFilled) && !isInvertedRange;
    }
    return isValidNumberString(value);
  }, [isRangeSelected, min, max, minFilled, maxFilled, isInvertedRange, value]);

  // Same condition that gates the error text below, also reflected on the field border.
  const hasVisibleInput = isRangeSelected
    ? !!(min.trim() || max.trim())
    : !!value.trim();
  const showFieldError = isInvertedRange || (!isValid && hasVisibleInput);

  const onInvalidChangeRef = useRef(onInvalidChange);
  onInvalidChangeRef.current = onInvalidChange;
  useEffect(() => {
    onInvalidChangeRef.current?.(showFieldError);
  }, [showFieldError]);
  useEffect(() => () => onInvalidChangeRef.current?.(false), []);

  const operatorLabels = useMemo(
    () => operators.map((op) => t(OPERATOR_LABELS[op])),
    [operators, t]
  );

  const handleOperatorSelect = useCallback(
    (e: MouseEvent<HTMLButtonElement>) => {
      const label = e.currentTarget.dataset.name;
      const nextOperator = operators.find(
        (op) => t(OPERATOR_LABELS[op]) === label
      );
      if (nextOperator) setOperator(nextOperator);
      setShowOperatorMenu(false);
    },
    [operators, t]
  );

  const handleOperatorFieldKeyDown = useCallback(
    (e: KeyboardEvent<HTMLDivElement>) => {
      if (e.key !== "Escape" || !showOperatorMenu) return;
      // Only the open menu closes; a host popover's own Escape handler
      // (NumericFilterButton) must not also close on the same key press.
      e.stopPropagation();
      setShowOperatorMenu(false);
    },
    [showOperatorMenu]
  );

  const criterion = useMemo<NumericFilterCriterion | null>(() => {
    if (!isValid) return null;
    return isRangeSelected
      ? {
          operator: operator as "between" | "overlaps",
          min: minFilled ? Number(min) : undefined,
          max: maxFilled ? Number(max) : undefined,
        }
      : {
          operator: operator as "is" | "is_exactly",
          value: Number(value),
        };
  }, [
    isValid,
    isRangeSelected,
    operator,
    min,
    max,
    minFilled,
    maxFilled,
    value,
  ]);

  const handleApply = useCallback(() => {
    if (criterion) onApply(criterion);
  }, [criterion, onApply]);

  const criterionKey = criterion
    ? encodeNumericFilterCriterion(criterion)
    : null;
  useEffect(() => {
    if (!inline || criterionKey === initialCriterionKey) return;
    selfAppliedKeyRef.current = criterionKey;
    if (criterion) {
      onApply(criterion);
    } else {
      onClear();
    }
    // Only the user's edits should trigger this, not callback identity changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [criterionKey]);

  return (
    <Wrapper $inline={inline}>
      <OperatorFieldWrapper
        ref={operatorFieldRef}
        onKeyDown={handleOperatorFieldKeyDown}
      >
        <OperatorTrigger
          type="button"
          data-testid={`numeric-filter-operator-${filterType}`}
          $open={showOperatorMenu}
          aria-haspopup="listbox"
          aria-expanded={showOperatorMenu}
          aria-label={t("Operator") as string}
          onClick={() => setShowOperatorMenu((prev) => !prev)}
        >
          <OperatorLabel>{t(OPERATOR_LABELS[operator])}</OperatorLabel>
          <OperatorIcon>
            <CaretDownIcon
              fill={theme.resolve("color_icon_tertiary")}
              width={16}
              height={16}
            />
          </OperatorIcon>
        </OperatorTrigger>
        {showOperatorMenu && (
          <OperatorMenuWrapper
            ref={operatorMenuRef}
            $openUp={operatorMenuOpenUp}
          >
            <DropDownModal
              className="list-dropdown"
              items={operatorLabels}
              activeItem={t(OPERATOR_LABELS[operator])}
              clickHandler={handleOperatorSelect}
            />
          </OperatorMenuWrapper>
        )}
      </OperatorFieldWrapper>

      {isRangeSelected ? (
        <ValueRow>
          <NumericInput
            data-testid={`numeric-filter-min-${filterType}`}
            type="text"
            placeholder={t("Min") as string}
            value={min}
            $invalid={showFieldError}
            onChange={(e) => setMin(e.target.value)}
          />
          <Text size="sm" color="secondary">
            {t("to")}
          </Text>
          <NumericInput
            data-testid={`numeric-filter-max-${filterType}`}
            type="text"
            placeholder={t("Max") as string}
            value={max}
            $invalid={showFieldError}
            onChange={(e) => setMax(e.target.value)}
          />
        </ValueRow>
      ) : (
        <NumericInput
          data-testid={`numeric-filter-value-${filterType}`}
          type="text"
          placeholder={t("Value") as string}
          value={value}
          $invalid={showFieldError}
          onChange={(e) => setValue(e.target.value)}
        />
      )}

      {!inline && (
        <ActionsRow>
          {initialCriterion && (
            <DestructiveButtonSmallV2
              testid={`numeric-filter-clear-${filterType}`}
              onClick={onClear}
              type="button"
            >
              {t("Clear")}
            </DestructiveButtonSmallV2>
          )}
          <PrimaryButtonSmallV2
            testid={`numeric-filter-apply-${filterType}`}
            onClick={handleApply}
            disabled={!isValid}
            type="button"
          >
            {t("Apply")}
          </PrimaryButtonSmallV2>
        </ActionsRow>
      )}
      {isInvertedRange && (
        <Text size="xs" color="error">
          {t("Min must be less than or equal to Max")}
        </Text>
      )}
      {!isInvertedRange && !isValid && hasVisibleInput && (
        <Text size="xs" color="error">
          {t(
            "Enter a valid number (decimal point, negative sign, and scientific notation are supported; commas are not)"
          )}
        </Text>
      )}
    </Wrapper>
  );
};

NumericFilterInput.displayName = "NumericFilterInput";

// Matches the fixed-width portal container `FilterBy` renders its own
// portalled dropdown into (see Filters.tsx's `#filter-dropdown-portal`) —
// used only to keep a portalled popover from overhanging the viewport edge,
// same purpose as `DROPDOWN_WIDTH` in FilterBy.tsx. Must
// stay in sync with `NumericFilterPopover`'s `width_xxs` token.
const PORTAL_POPOVER_WIDTH = 320;

interface INumericFilterButton {
  /** The filter key (e.g. a collection column id) this button controls. */
  filterType: string;
  /** The attribute's display name, shown on the trigger button. */
  label: string;
  /** Which operators to offer — see `INumericFilterInput.operators`. */
  operators: NumericFilterOperator[];
  preselectedFilters: ChipType[];
  setter: ({ values, filter }: { values: ChipType[]; filter: string }) => void;
  /**
   * Mirrors `FilterBy`'s `usePortal` — render the popover into the
   * `filter-dropdown-portal` container (see Filters.tsx), or `document.body`
   * when that container isn't mounted, instead of in-flow, so it isn't
   * clipped by an ancestor with `overflow: hidden`/`auto` (e.g. Filters.tsx's
   * collapsed filter row, or `TablePageLayout`'s scrolling content wrappers
   * around a collection table's filter row).
   */
  usePortal?: boolean;
  /**
   * Mirrors `FilterBy`'s `isLastVisible` — fades out the trigger button so
   * a "+N more" toggle rendered on top of it isn't stacked on a visible
   * button.
   */
  isLastVisible?: boolean;
  /** Forwarded to the trigger button — e.g. Filters.tsx's `.button-item`
   *  marker, which its "+N more" fold calculation queries for. */
  className?: string;
}

/**
 * AP-1016 (Advanced Filters, Part 1) — a `FilterBy`-shaped dropdown-button
 * wrapper around `NumericFilterInput`, for surfaces (collection table column
 * headers, see ProductCollectionSchemaTable.tsx, and Filters.tsx's filter
 * row) that render one filter trigger per column/facet rather than
 * FiltersFlyBar's accordion shell. Encodes/decodes the criterion as a single
 * synthetic chip so it can be handed to the same `setter` shape `FilterBy`
 * already uses, with no change required to callers' filter-state plumbing.
 */
export const NumericFilterButton = ({
  filterType,
  label,
  operators,
  preselectedFilters,
  setter,
  usePortal = false,
  isLastVisible,
  className,
}: INumericFilterButton) => {
  const [showDropDown, setShowDropDown] = useState(false);
  const [openedFromKeyboard, setOpenedFromKeyboard] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  // Bound to the popover content itself, not the trigger button — a
  // portalled popover isn't a DOM descendant of the button, so pinning this
  // to the button (or a wrapper containing both) would make every click
  // inside the popover register as "outside" and close it instantly. This
  // mirrors `FilterBy`'s own `wrapperRef` placement for the same reason.
  // Presses on the trigger are left to its own toggle, otherwise mousedown
  // would close the popover and the following click would reopen it.
  const popoverRef = useRef<HTMLDivElement>(null);
  useOnClickOutside(popoverRef, (event: Event) => {
    if (
      event.target instanceof Node &&
      buttonRef.current?.contains(event.target)
    ) {
      return;
    }
    setShowDropDown(false);
  });
  const closeDropDown = useCallback(() => setShowDropDown(false), []);
  const portalStyle = usePortalPopoverPosition({
    isOpen: showDropDown && usePortal,
    triggerRef: buttonRef,
    popoverRef,
    popoverWidth: PORTAL_POPOVER_WIDTH,
    onTriggerHidden: closeDropDown,
  });
  const { restoreFocusOnClose, onPopoverKeyDown } = usePopoverFocus({
    isOpen: showDropDown && usePortal,
    triggerRef: buttonRef,
    popoverRef,
    onClose: closeDropDown,
    openedFromKeyboard,
  });

  const criterion = decodeNumericFilterCriterion(preselectedFilters?.[0]?.name);

  const handleApply = useCallback(
    (next: NumericFilterCriterion) => {
      const chip: ChipType = {
        name: encodeNumericFilterCriterion(next),
        type: "numeric",
      };
      setter({ values: [chip], filter: filterType });
      restoreFocusOnClose();
      closeDropDown();
    },
    [setter, filterType, restoreFocusOnClose, closeDropDown]
  );

  const handleClear = useCallback(() => {
    setter({ values: [], filter: filterType });
    restoreFocusOnClose();
    closeDropDown();
  }, [setter, filterType, restoreFocusOnClose, closeDropDown]);

  const popover = (
    <NumericFilterPopover
      ref={popoverRef}
      style={usePortal ? portalStyle : undefined}
      tabIndex={usePortal ? -1 : undefined}
      onKeyDown={usePortal ? onPopoverKeyDown : undefined}
    >
      <NumericFilterInput
        filterType={filterType}
        operators={operators}
        initialCriterion={criterion}
        onApply={handleApply}
        onClear={handleClear}
      />
    </NumericFilterPopover>
  );

  return (
    <DropDownWrapper size="small">
      <FilterButton
        size="small"
        active={!!criterion || showDropDown}
        ref={buttonRef}
        data-testid={`numeric-filter-trigger-${filterType}`}
        className={className}
        style={
          isLastVisible ? { opacity: 0, pointerEvents: "none" } : undefined
        }
        onClick={(event: MouseEvent<HTMLButtonElement>) => {
          if (showDropDown) {
            restoreFocusOnClose();
          } else {
            setOpenedFromKeyboard(event.detail === 0);
          }
          setShowDropDown(!showDropDown);
        }}
      >
        {criterion ? `${label} (1)` : label}
      </FilterButton>
      {showDropDown &&
        (usePortal
          ? ReactDOM.createPortal(
              popover,
              document.getElementById("filter-dropdown-portal") ?? document.body
            )
          : popover)}
    </DropDownWrapper>
  );
};

NumericFilterButton.displayName = "NumericFilterButton";
